import { ReactElement, SyntheticEvent, useEffect } from 'react'
import { shallowEqual, useSelector, useDispatch } from 'react-redux'
import { RootState } from '../../../../../redux/store'
import {
  FormWrapper,
  StyledButton,
  CardContainer,
  ButtonWrapper
} from '../subcomponents'
import strings from '../../../../../locale/en'
import { PackageWrapper, Title } from '../../subcomponents'
import {
  completeRegistration,
  updateHasError,
  updatePaymentOrderFailed
} from '../../../../../redux/register/action'
import { Loader, TostMessage } from '../../../../../components'
import BREAKPOINTS from '../../../../../const/breakpoint'
import useBreakpoint from 'use-breakpoint'
import createPaymentOrderId from '../../../../../services/createPaymentOrderId'
import { loadRazorPayParam } from '../../../typings'
import useRazorpay from 'react-razorpay'
import theme from '../../../../../const/theme'
import { Container, BenefitsList, BenefitsListItem } from './subcomponents'
import ROUTES from '../../../../../const/routes'
import { ROLE } from '../../../../../helpers/determineMenu'
import { useHistory } from 'react-router-dom'

const PurchaseCourse = (): ReactElement => {
  const {
    privateCourses,
    isLoading,
    hasError = false,
    userDetails,
    isLoggedIn,
    userInfo
  } = useSelector(
    (state: RootState) => ({
      privateCourses: state.register.privateCourses,
      isLoading: state.register.isLoading,
      hasError: state.register.hasError,
      userDetails: state.register.userDetails,
      isLoggedIn: state.user.isLoggedIn,
      userInfo: state.user.userInfo
    }),
    shallowEqual
  )
  const dispatch = useDispatch()
  const Razorpay = useRazorpay()
  const history = useHistory()
  const selectedCourses = privateCourses.filter((course) => course.selected)
  const { breakpoint } = useBreakpoint(BREAKPOINTS)

  const {
    register: {
      buy,
      benefitsOffered,
      completeRegistrationFailed,
      getTrial,
      benefits: { learnModuleAccess, liveModuleAccess, testModuleAccess }
    }
  } = strings

  const loadRazorPay = ({ orderId, amount }: loadRazorPayParam) => {
    const options = {
      key: `${process.env.REACT_APP_RAZORPAY_KEY}`,
      amount: `${amount}`,
      currency: 'INR',
      name: 'Upmyranks',
      order_id: orderId,
      handler: function (response: {
        razorpay_payment_id: any
        razorpay_order_id: any
        razorpay_signature: any
      }) {
        const data = {
          ...userDetails,
          paid: true,
          trial: false,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_order_id: response.razorpay_order_id,
          razorpay_signature: response.razorpay_signature,
          courses: selectedCourses.map((course) => course?.value).join(),
          coachingCenterId: selectedCourses
            .map((course) => course?.coachingCentreId)
            .join(),
          loginDevice: breakpoint === 'desktop' ? 'website' : 'mobile'
        }
        dispatch(completeRegistration(data))
      },
      prefill: {
        name: userDetails.name,
        email: userDetails.email,
        contact: userDetails.phone
      },
      theme: {
        color: theme?.appBackground
      },
      modal: {
        ondismiss: function () {
          dispatch(updatePaymentOrderFailed(orderId))
        }
      }
    }
    const rzp1 = new Razorpay(options)

    rzp1.open()
  }

  const purchaseWithTrial = (e: SyntheticEvent) => {
    e.preventDefault()
    const data = {
      ...userDetails,
      paid: false,
      trial: true,
      courses: selectedCourses.map((course) => course?.value).join(),
      coachingCenterId: selectedCourses
        .map((course) => course?.coachingCentreId)
        .join(),
      loginDevice: breakpoint === 'desktop' ? 'website' : 'mobile'
    }
    dispatch(completeRegistration(data))
  }

  const purchaseWithPayment = async (e: SyntheticEvent) => {
    e.preventDefault()
    let totalCourseAmount = 0
    selectedCourses.forEach((course) => {
      totalCourseAmount = totalCourseAmount + Number(course?.amount)
    })
    const orderId = await createPaymentOrderId({
      user: userDetails.name,
      amount: totalCourseAmount
    })
    loadRazorPay({
      amount: totalCourseAmount,
      orderId: orderId
    })
  }

  useEffect(() => {
    if (isLoggedIn) {
      switch (userInfo?.role) {
        case ROLE.STUDENT:
          history.push(ROUTES.LEARN)
          break
        case ROLE.ADMIN:
          history.push(ROUTES.DASHBOARD)
          break
        case ROLE.INSTITUTE_ADMIN:
          history.push(ROUTES.BRANCHADMIN)
          break
        case ROLE.TEACHER_ADMIN:
          history.push(ROUTES.PUBLISH_LIST)
          break
        default:
          history.push(ROUTES.TEACHER)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn])

  return (
    <PackageWrapper>
      <Container>
        <TostMessage
          show={hasError}
          onCloseHandler={() => {
            dispatch(updateHasError(false))
            history.push(ROUTES.LOGIN)
          }}
          message={completeRegistrationFailed}
        ></TostMessage>
        <FormWrapper onSubmit={purchaseWithPayment}>
          <Title>{benefitsOffered}</Title>
          <CardContainer>
            {isLoading ? (
              <Loader />
            ) : (
              <BenefitsList as="ol">
                <BenefitsListItem as="li">{learnModuleAccess}</BenefitsListItem>
                <BenefitsListItem as="li">{liveModuleAccess}</BenefitsListItem>
                <BenefitsListItem as="li">{testModuleAccess}</BenefitsListItem>
              </BenefitsList>
            )}
          </CardContainer>

          <ButtonWrapper>
            <StyledButton
              className="mb-3 mt-3"
              variant="secondary"
              type="button"
              onClick={purchaseWithTrial}
            >
              {getTrial}
            </StyledButton>
            <StyledButton className="mb-3 mt-3" variant="primary" type="submit">
              {buy}
            </StyledButton>
          </ButtonWrapper>
        </FormWrapper>
      </Container>
    </PackageWrapper>
  )
}

export default PurchaseCourse
